'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/shared/utils/cn';
import { Check } from 'lucide-react';

interface WizardStepperProps {
  steps: string[];
  currentStep: number;
}

export function WizardStepper({ steps, currentStep }: WizardStepperProps) {
  const t = useTranslations('content.wizard.steps');
  const tStepper = useTranslations('content.wizard.stepper');

  const total = steps.length;
  const clamped = Math.min(Math.max(currentStep, 1), total);
  const currentKey = steps[clamped - 1];
  const pct = total > 0 ? (clamped / total) * 100 : 0;

  return (
    <nav
      aria-label="Wizard progress"
      data-testid="content-wizard-stepper"
    >
      {/* Compact progress for small screens — no horizontal scroll. */}
      <div className="sm:hidden" data-testid="content-wizard-stepper-compact">
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            {currentKey ? t(currentKey) : null}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {tStepper('stepOf', { current: clamped, total })}
          </span>
        </div>
        <div className="flex items-center gap-1" aria-hidden>
          {steps.map((stepKey, index) => (
            <div
              key={stepKey}
              className={cn(
                'h-1.5 flex-1 rounded-full transition-colors',
                index < clamped ? 'bg-primary' : 'bg-muted',
              )}
            />
          ))}
        </div>
      </div>

      {/* Full labeled chain where it fits. */}
      <div className="hidden items-center justify-center gap-4 py-1 sm:flex">
        {steps.map((stepKey, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === clamped;
          const isCompleted = stepNumber < clamped;

          return (
            <div key={stepKey} className="flex items-center gap-4">
              {index > 0 && (
                <div
                  className={cn(
                    'h-px w-10',
                    isCompleted ? 'bg-primary' : 'bg-border',
                  )}
                />
              )}
              <div className="flex items-center gap-2 shrink-0">
                <div
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors',
                    isActive && 'bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-2 ring-offset-background',
                    isCompleted && 'bg-primary/80 text-primary-foreground',
                    !isActive && !isCompleted && 'bg-muted text-muted-foreground',
                  )}
                >
                  {isCompleted ? <Check className="size-4" /> : stepNumber}
                </div>
                <span
                  className={cn(
                    'text-xs whitespace-nowrap',
                    isActive ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {t(stepKey)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
